import {
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { inject, Injectable, makeStateKey, TransferState } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, EMPTY, map, Observable, of, tap, throwError } from 'rxjs';
import { ApiService } from '@core/services/api.service';
import { ApiStateService } from '@core/services/api-state.service';
import { DataService } from '@core/services/data.service';
import { ApiUrl } from '@shared/classes/api-url.class';
import { CampaignTypes, Pages } from '@shared/constants';
import {
  AnyChallengeDTO,
  ChallengeBaseDTO,
  CouponDTO,
  ProductDTO,
  RewardDto,
  TotalRewardDTO,
} from '@shared/dto';
import { Coupon, Product, Reward, TotalReward } from '@shared/factory';
import {
  AuthPayload,
  AuthResponse,
  CampaignsByPhase,
  CheckRecentPlayerResponse,
  SendClaimResponse,
} from '@shared/models';
import { AbTestData } from '@shared/models/ab-test.models';
import { isUntienotsIdValid } from '@shared/utils';

interface GetCouponsOptions {
  redirectOnError?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class BackendService {
  public untienotsId: string;

  private readonly router = inject(Router);
  private readonly apiService = inject(ApiService);
  private readonly dataService = inject(DataService);
  private readonly apiState = inject(ApiStateService);
  private readonly transferState = inject(TransferState);

  private readonly BACKEND_URL: string = new ApiUrl().apiUrl;

  // #region - Loyalty Challenge API calls
  login(payload: AuthPayload): Observable<AuthResponse> {
    return this.apiService.post<AuthResponse>(
      `${this.BACKEND_URL}/signin`,
      payload
    );
  }

  loadCampaign(campaignType: CampaignTypes): Observable<CampaignsByPhase> {
    if (this.transferState.hasKey(this.apiState.CAMPAIGNS_KEY[campaignType])) {
      const cached = this.transferState.get(
        this.apiState.CAMPAIGNS_KEY[campaignType],
        null as unknown as CampaignsByPhase
      );
      return of(cached);
    }

    return this.apiService
      .get<CampaignsByPhase>(
        `${this.BACKEND_URL}/core/campaigns?campaign_type=${campaignType}`
      )
      .pipe(
        tap((data: CampaignsByPhase) =>
          this.transferState.set(
            this.apiState.CAMPAIGNS_KEY[campaignType],
            data
          )
        ),
        catchError((error: HttpErrorResponse) => {
          console.error('Error fetching campaigns', error);
          this.handleRedirection();
          return EMPTY;
        })
      );
  }

  getChallenges(
    campaignId: number = this.dataService.currentCampaign().campaignId
  ): Observable<AnyChallengeDTO[]> {
    // Ensure a valid state key always exists
    if (typeof this.apiState.CHALLENGES_KEY[campaignId] === 'undefined') {
      this.apiState.CHALLENGES_KEY[campaignId] = makeStateKey<
        AnyChallengeDTO[]
      >(`challenges-${campaignId}`);
    }

    const cacheKey = this.apiState.CHALLENGES_KEY[campaignId];

    // Check for SSR transfer cache (typically on browser)
    if (this.transferState.hasKey(cacheKey)) {
      const cached = this.transferState.get(
        cacheKey,
        [] as unknown as AnyChallengeDTO[]
      );
      return of(cached);
    }

    // Validate customer ID
    if (!isUntienotsIdValid(this.untienotsId)) {
      console.warn('Invalid untienotsId');
      this.handleRedirection();
      return EMPTY;
    }

    // Fetch and process API response
    const url = `${this.BACKEND_URL}/core/customers/${this.untienotsId}/campaigns/${campaignId}/challenges`;

    return this.apiService.get<AnyChallengeDTO[]>(url).pipe(
      tap((data: AnyChallengeDTO[]) =>
        this.transferState.set(this.apiState.CHALLENGES_KEY[campaignId], data)
      ),
      catchError((error: HttpErrorResponse) => {
        console.error('Error fetching challenges', error);

        const isCurrent = this.router.url.includes(Pages.CURRENT);
        const isSoon = this.router.url.includes(Pages.SOON);
        const currentCampaignID = this.dataService.currentCampaign().campaignId;

        if (isSoon) {
          this.router.navigate([this.untienotsId, Pages.CURRENT], {
            queryParamsHandling: 'preserve',
          });
        } else if (isCurrent && currentCampaignID === campaignId) {
          this.handleRedirection();
        }

        return EMPTY;
      })
    );
  }

  getABTest(campaignId: number, untienotsId: string): Observable<AbTestData> {
    if (!isUntienotsIdValid(untienotsId)) {
      console.error('Invalid untienotsId');
      return of({} as AbTestData);
    }

    return this.apiService
      .get<AbTestData>(
        `${this.BACKEND_URL}/campaigns/${campaignId}/abtest/${untienotsId}`
      )
      .pipe(
        catchError((error: HttpErrorResponse) => {
          console.error('Error fetching AB Test data', error);
          return of({} as AbTestData);
        })
      );
  }

  selectChallenge(
    campaignId: number,
    offerId: number
  ): Observable<ChallengeBaseDTO> {
    if (!isUntienotsIdValid(this.untienotsId)) {
      console.error('Invalid untienotsId');
      return EMPTY;
    }
    return this.apiService.post<ChallengeBaseDTO>(
      `${this.BACKEND_URL}/customers/${this.untienotsId}/campaigns/${campaignId}/challenges/${offerId}/select`,
      { timezone: Intl.DateTimeFormat().resolvedOptions().timeZone }
    );
  }

  unselectChallenge(
    campaignId: number,
    offerId: number
  ): Observable<ChallengeBaseDTO> {
    if (!isUntienotsIdValid(this.untienotsId)) {
      console.error('Invalid untienotsId');
      return EMPTY;
    }
    return this.apiService.post<ChallengeBaseDTO>(
      `${this.BACKEND_URL}/customers/${this.untienotsId}/campaigns/${campaignId}/challenges/${offerId}/unselect`
    );
  }

  getTotalReward(
    campaignId: number = this.dataService.currentCampaign().campaignId
  ): Observable<number> {
    if (typeof campaignId === 'undefined') {
      return EMPTY;
    }

    if (typeof this.apiState.TOTAL_REWARDS_KEY[campaignId] === 'undefined') {
      this.apiState.TOTAL_REWARDS_KEY[campaignId] = makeStateKey<number>(
        `total-rewards-${campaignId}`
      );
    }

    const cacheKey = this.apiState.TOTAL_REWARDS_KEY[campaignId];

    if (this.transferState.hasKey(cacheKey)) {
      const cached = this.transferState.get(cacheKey, 0);
      return of(cached);
    }

    return this.apiService
      .get<TotalRewardDTO>(
        `${this.BACKEND_URL}/core/customers/${this.untienotsId}/campaigns/${campaignId}/total-reward`
      )
      .pipe(
        map((dto: TotalRewardDTO) => new TotalReward(dto).totalReward),
        tap((totalReward: number) =>
          this.transferState.set(cacheKey, totalReward)
        ),
        catchError((error: HttpErrorResponse) => {
          console.error('Error fetching total-rewards', error);
          return EMPTY;
        })
      );
  }

  getLastPurchase(): Observable<string> {
    if (this.transferState.hasKey(this.apiState.LAST_PURCHASE_KEY)) {
      const cached = this.transferState.get<string>(
        this.apiState.LAST_PURCHASE_KEY,
        ''
      );
      return of(cached);
    }

    if (!isUntienotsIdValid(this.untienotsId)) {
      console.error('Invalid untienotsId');
      return EMPTY;
    }

    return this.apiService
      .get<string>(
        `${this.BACKEND_URL}/core/customers/${this.untienotsId}/last-purchase`
      )
      .pipe(
        map((lastPurchaseDate: string) => {
          // If API returns nothing, don't show a date.
          if (!lastPurchaseDate || !lastPurchaseDate.length) {
            return '';
          }
          const purchaseDate = new Date(lastPurchaseDate);
          if (isNaN(purchaseDate.getTime())) {
            return '';
          }
          // Return normalized ISO string; formatting is handled in config.
          return purchaseDate.toISOString();
        }),
        tap((lastPurchaseDate: string) =>
          this.transferState.set(
            this.apiState.LAST_PURCHASE_KEY,
            lastPurchaseDate
          )
        ),
        catchError((error: HttpErrorResponse) => {
          console.error('Error fetching last-purchase', error);
          return EMPTY;
        })
      );
  }

  checkIsRecentPlayer(): Observable<boolean> {
    if (this.transferState.hasKey(this.apiState.RECENT_PLAYER_KEY)) {
      const cached = this.transferState.get<boolean>(
        this.apiState.RECENT_PLAYER_KEY,
        false
      );
      return of(cached);
    }

    if (!isUntienotsIdValid(this.untienotsId)) {
      console.error('Invalid untienotsId');
      return EMPTY;
    }

    return this.apiService
      .get<CheckRecentPlayerResponse>(
        `${this.BACKEND_URL}/core/customers/${this.untienotsId}/has-played-recently`
      )
      .pipe(
        map((data: CheckRecentPlayerResponse) => data.has_played_recently),
        tap((isRecentPlayer: boolean) =>
          this.transferState.set(
            this.apiState.RECENT_PLAYER_KEY,
            isRecentPlayer
          )
        ),
        catchError((error: HttpErrorResponse) => {
          console.error('Error checking is recent player ', error);
          return EMPTY;
        })
      );
  }

  getProductsList(productScopeId: number): Observable<Product[]> {
    return (
      this.apiService
        .get<ProductDTO[]>(
          `${this.BACKEND_URL}/core/product-scopes/${productScopeId}/products`
        )
        // Errors are left to propagate (not swallowed into an empty list) so an
        // eligibility check against the list reports a failure, not "not eligible".
        // Logged on the way through, since nothing downstream logs them.
        .pipe(
          map((products: ProductDTO[]) =>
            products.map((p: ProductDTO) => new Product(p))
          ),
          // `unknown`, not `HttpErrorResponse`: an error thrown by the `map`
          // above (a malformed DTO) flows through here too.
          catchError((error: unknown) => {
            console.error('Error fetching the product list ', error);
            return throwError(() => error);
          })
        )
    );
  }

  /**
   * Send claim contact form
   *
   * @param content claim content
   * @param untienotsId customer id
   * @param name name
   * @param email email
   * @param phone phone
   * @param surname surname
   * @param bearerToken token
   */
  sendClaim(
    content: string,
    untienotsId: string,
    name = '',
    email = '',
    phone = '',
    surname = '',
    bearerToken = ''
  ): Observable<SendClaimResponse> {
    const payload: {
      email: string;
      phone: string;
      message: string;
      lastname: string;
      firstname: string;
      untienots_id?: string;
    } = {
      phone: phone,
      email: email,
      firstname: name,
      message: content,
      lastname: surname,
      untienots_id: untienotsId,
    };

    const url = `${this.BACKEND_URL}/core/claims`;

    const httpParams = new HttpParams({
      fromObject: {
        origin: 'b2c-frontend',
      },
    });

    let httpHeaders: HttpHeaders = new HttpHeaders();
    if (bearerToken) {
      httpHeaders = new HttpHeaders({
        Authorization: bearerToken,
        'Content-Type': 'application/json',
      });
    }

    return this.apiService.post<SendClaimResponse>(
      url,
      payload,
      httpParams,
      httpHeaders
    );
  }

  sendOptins(): Observable<null> {
    return this.apiService.post<null>(
      `${this.BACKEND_URL}/optins/${this.untienotsId}`
    );
  }

  getOptins(): Observable<{ optin: boolean }> {
    return this.apiService.get<{ optin: boolean }>(
      `${this.BACKEND_URL}/optins/${this.untienotsId}`
    );
  }
  // #endregion

  // #region - Rewards API calls
  getRewards(): Observable<Reward[]> {
    if (this.transferState.hasKey(this.apiState.REWARDS_KEY)) {
      const cached = this.transferState.get(this.apiState.REWARDS_KEY, []);
      return of(cached);
    }

    if (!isUntienotsIdValid(this.untienotsId)) {
      console.error('Invalid untienotsId');
      this.handleRedirection();
      return EMPTY;
    }

    return this.apiService
      .get<RewardDto[]>(
        `${this.BACKEND_URL}/customers/${this.untienotsId}/burn-rewards/available`
      )
      .pipe(
        map((rewards: RewardDto[]) =>
          rewards.map((r: RewardDto) => new Reward(r))
        ),
        tap((data: Reward[]) =>
          this.transferState.set(this.apiState.REWARDS_KEY, data)
        ),
        catchError((error: HttpErrorResponse) => {
          console.error('Error fetching rewards', error);
          this.handleRedirection();
          return EMPTY;
        })
      );
  }

  getPurchasedRewards(): Observable<Reward[]> {
    if (this.transferState.hasKey(this.apiState.PURCHASED_REWARDS_KEY)) {
      const cached = this.transferState.get(
        this.apiState.PURCHASED_REWARDS_KEY,
        []
      );
      return of(cached);
    }

    if (!isUntienotsIdValid(this.untienotsId)) {
      console.error('Invalid untienotsId');
      this.handleRedirection();
      return EMPTY;
    }

    return this.apiService
      .get<RewardDto[]>(
        `${this.BACKEND_URL}/customers/${this.untienotsId}/burn-rewards/activated`
      )
      .pipe(
        map((rewards: RewardDto[]) =>
          rewards.map((r: RewardDto) => new Reward(r))
        ),
        tap((data: Reward[]) =>
          this.transferState.set(this.apiState.PURCHASED_REWARDS_KEY, data)
        ),
        catchError((error: HttpErrorResponse) => {
          console.error('Error fetching purchased rewards', error);
          this.handleRedirection();
          return EMPTY;
        })
      );
  }

  activateReward(rewardId: number): Observable<Reward> {
    if (!isUntienotsIdValid(this.untienotsId)) {
      console.error('Invalid untienotsId');
      return EMPTY;
    }

    return this.apiService
      .post<RewardDto>(
        `${this.BACKEND_URL}/customers/${this.untienotsId}/burn-rewards/${rewardId}/activate`
      )
      .pipe(map((r: RewardDto) => new Reward(r)));
  }
  // #endregion

  // #region - Coupons API calls
  getCoupons(
    untienotsId: string,
    campaignId: number,
    options: GetCouponsOptions = {}
  ): Observable<Coupon[]> {
    return this.apiService
      .get<CouponDTO[]>(
        `${this.BACKEND_URL}/customers/${untienotsId}/campaigns/${campaignId}/coupons`
      )
      .pipe(
        map((coupons: CouponDTO[]) =>
          coupons.map((c: CouponDTO) => new Coupon(c))
        ),
        catchError((error: HttpErrorResponse) => {
          console.error('Error loading coupons', error);
          if (options.redirectOnError !== false) {
            this.handleRedirection();
            return EMPTY;
          }
          return of([]);
        })
      );
  }

  /**
   * Rewards the customer earned since the start of the calendar year.
   *
   * @returns the amount, or `null` when it could not be fetched — the wallet
   *   then leaves the yearly figure out rather than showing a false 0.
   */
  getYearlyReward(): Observable<number | null> {
    if (!isUntienotsIdValid(this.untienotsId)) {
      console.error('Invalid untienotsId');
      return of(null);
    }

    return this.apiService
      .get<TotalRewardDTO>(
        `${this.BACKEND_URL}/core/customers/${this.untienotsId}/yearly-reward`
      )
      .pipe(
        map((dto: TotalRewardDTO) => new TotalReward(dto).totalReward),
        catchError((error: HttpErrorResponse) => {
          console.error('Error fetching yearly-reward', error);
          return of(null);
        })
      );
  }

  loadCoupon(
    offerId: number,
    campaignId: number
  ): Observable<{ offer_id: number; loading_date: string }> {
    return this.apiService.post<{ offer_id: number; loading_date: string }>(
      `${this.BACKEND_URL}/customers/${this.untienotsId}/campaigns/${campaignId}/coupons/${offerId}/activate`
    );
  }

  /**
   * Server-side eligibility check for a single barcode. The backend looks the
   * barcode up in the offer's product scopes *and* categories, so this one
   * endpoint covers both — categories can hold several thousand products, which
   * is why we check a barcode rather than fetch the whole list.
   *
   * The barcode is passed as a query param (not a path segment) so the backend
   * can also resolve partial input.
   *
   * @param campaignId campaign the offer belongs to
   * @param offerId offer whose scope/category the barcode is checked against
   * @param barcode scanned or typed barcode
   * @returns the matching {@link Product} when eligible, or `null` when the
   *   barcode isn't part of the offer (the backend answers 404 in that case).
   */
  getEligibleProduct(
    campaignId: number,
    offerId: number,
    barcode: string
  ): Observable<Product | null> {
    const params: HttpParams = new HttpParams().set('barcode', barcode);
    return this.apiService
      .get<ProductDTO | null>(
        `${this.BACKEND_URL}/campaigns/${campaignId}/offers/${offerId}/products`,
        params
      )
      .pipe(
        map((product: ProductDTO | null) =>
          product ? new Product(product) : null
        ),
        catchError((error: unknown) => {
          // 404 is the backend's "not eligible" answer; anything else is a
          // real failure the caller must distinguish from a verdict.
          if (error instanceof HttpErrorResponse && error.status === 404) {
            return of(null);
          }
          return throwError(() => error);
        })
      );
  }
  // #endregion;

  // #region - Helper methods for coupon sorting and error handling
  private handleRedirection(): void {
    const { availablePages } = this.dataService.config().pages;
    const fallback = availablePages.includes(Pages.LOGIN)
      ? Pages.LOGIN
      : Pages.SORRY;

    this.router.navigate([fallback], {
      queryParamsHandling: 'preserve',
    });
  }
  // #endregion
}
